<template>
  <section id="hero" class="o-section c-hero" v-my-section-class>
    <div class="o-background">
      <div class="o-background__inner">
        <div class="c-hero__video" v-if="!cannotSupportVideo">
          <video ref="video" muted playsinline webkit-playsinline v-if="videoShown" @ended="videoEnded" @canplaythrough="prepareVideo">
            <source :src="'https://player.vimeo.com/external/237554818.hd.mp4?s=525903b7d5ad28552375f1ae18599f58d62c93c0&profile_id=175'" type="video/mp4">
          </video>
          <div class="c-hero__video__cover" v-if="!videoReady"></div>
          <transition name="fade">
            <div class="c-hero__video__final-frame" v-if="staticShown"></div>
          </transition>
        </div>

        <svg ref="svg" :viewbox="'0 0 ' + viewport.width + ' ' + viewport.height " :width="viewport.width" :height="viewport.height"></svg>
        <template v-if="cannotSupportVideo">
          <my-circle ref="circ1" :style="[circ1Style]" class="o-background__item o-background__item--1" :variant="2" :image-file="'circles/hero-1a'" :is-large="true"></my-circle>
          <my-circle ref="circ2" :style="[circ2Style]" class="o-background__item o-background__item--2" :image-file="'circles/hero-1b'"></my-circle>
        </template>
      </div>
    </div>
    <div class="o-section__container c-hero__container">
      <div class="c-hero__content" ref="content">
        <div class="c-hero__text">
          <h2 class="o-section__title c-hero__title">
            <span class="c-hero__title__inner">
              <span class="u-hide">Transforming power delivery</span>
            </span>
          </h2>
          <p class="c-hero_subtitle">Annual Review 2016/17</p>
        </div>
        <my-scroll class="c-hero__button o-link--round" :href="'#ceo-introduction'">
          <span class="c-hero__button__inner">
            <svg class="icon icon-down-arrow-large"><use xlink:href="#icon-down-arrow-large"></use></svg>
          </span>
        </my-scroll>
      </div>
    </div>
  </section>
</template>

<script>
import { select } from 'd3-selection'
import { scaleLinear } from 'd3-scale'
import { range, min, max } from 'd3-array'
import MyScroll from '@/components/standard/my-scroll'
import MyCircle from '@/components/standard/my-circle'
import { MySectionClass } from '@/directives/my-section-class'
import MobileDetect from 'mobile-detect/mobile-detect'

export default {
  name: 'my-hero',
  components: {
    MyScroll,
    MyCircle
  },
  directives: {
    MySectionClass
  },
  data () {
    return {
      maxHeight: 1080,
      svg: null,
      staticShown: false,
      videoShown: true,
      videoReady: false,
      md: new MobileDetect(window.navigator.userAgent)
    }
  },
  computed: {
    viewport () {
      return this.$store.state.viewport
    },
    device () {
      return this.$store.state.device
    },
    cannotSupportVideo () {
      // return this.device !== 0 || !(this.md.is('iOS') && this.md.version('iOS') < 10)
      return this.device === 0 || this.md.is('iOS')
    },
    colors () {
      return this.$store.state.colors
    },
    coord () {
      return (this.device !== 0) ? { x1: -480, y1: 140, x2: 380, y2: 380 } : { x1: -250, y1: 0, x2: 180, y2: 250 }
    },
    circ1Style () {
      return {
        transform: 'translate(' + this.getTrueCoord(this.coord.x1) + 'px, ' + this.coord.y1 + 'px)'
      }
    },
    circ2Style () {
      return {
        transform: 'translate(' + this.getTrueCoord(this.coord.x2) + 'px, ' + this.coord.y2 + 'px)'
      }
    }
  },
  methods: {
    setHeight () {
      let contentHeight = this.$refs.content.clientHeight
      let viewportHeight = window.innerHeight
      let offset = (this.$store.state.device !== 0) ? 0 : 60
      if (viewportHeight > contentHeight + offset) {
        let newHeight = (viewportHeight < this.maxHeight) ? viewportHeight + 1 : this.maxHeight + 1
        this.$el.style.height = (newHeight - offset) + 'px'
      }
    },
    getTrueCoord (coord) {
      return coord + (this.$store.state.viewport.width / 2)
    },
    initLine () {
      let diam = (this.device !== 0) ? 16 : 10
      let num = 17
      let data = range(num)

      let x = scaleLinear()
        .range([this.getTrueCoord(this.coord.x1), this.getTrueCoord(this.coord.x2)])
        .domain([min(data, (d) => { return d }), max(data, (d) => { return d })])
      let y = scaleLinear()
        .range([this.coord.y1, this.coord.y2])
        .domain([min(data, (d) => { return d }), max(data, (d) => { return d })])
      this.svg.selectAll('circle')
        .data(data)
        .enter().append('circle')
        .attr('class', 'c-hero__dot')
        .attr('r', diam)
        .attr('cx', (d) => { return Math.round(x(d)) })
        .attr('cy', (d) => { return Math.round(y(d)) })
    },
    defineGradients () {
      // Define gradient
      let svgDefs = this.svg.append('defs')
      this.colors.forEach((d, i) => {
        let gradient1 = svgDefs.append('linearGradient')
          .attr('id', 'o-chart--' + d + '--h')
        gradient1.append('stop')
          .attr('class', 'o-chart__stop-top')
          .attr('offset', '0')
        gradient1.append('stop')
          .attr('class', 'o-chart__stop-bottom')
          .attr('offset', '1')

        let gradient2 = svgDefs.append('linearGradient')
          .attr('id', 'o-chart--' + d + '--v')
          .attr('x2', '0%')
          .attr('y2', '100%')
        gradient2.append('stop')
          .attr('class', 'o-chart__stop-top')
          .attr('offset', '0')
        gradient2.append('stop')
          .attr('class', 'o-chart__stop-middle')
          .attr('offset', '0.5')
        gradient2.append('stop')
          .attr('class', 'o-chart__stop-bottom')
          .attr('offset', '1')
      })
    },
    videoEnded () {
      this.staticShown = true
      setTimeout(() => {
        this.videoShown = false
      }, 1000)
    },
    prepareVideo () {
      if (!this.videoReady) {
        this.$refs.video.play()
        this.videoReady = true
      }
    }
  },
  mounted () {
    this.setHeight()
    this.svg = select(this.$refs.svg)
    this.defineGradients()
    if (this.cannotSupportVideo) {
      this.initLine()
    }
  },
  watch: {
    device (val) {
      this.svg.selectAll('.c-hero__dot').remove()
      if (this.cannotSupportVideo) {
        this.initLine()
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// MyHero.vue?58fe96b8